import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Company, CompanyBasic } from '~hooks/useCompany/models';
import useManagedCompanies from '~hooks/useManagedCompanies/useManagedCompanies';
import { Pagination } from '~services/pagination';

import ManageAccountsForm from './ManageAccountsForm';

type ManagedCompaniesState = {
  companies: Company[];
  isFormOpen: boolean;
  isLoading: boolean;
  pagination?: Pagination;
};

const selectedCompanySchema = yup.object().shape({
  company: yup.mixed<CompanyBasic>().nullable(),
});
type SelectedCompanyDTO = yup.InferType<typeof selectedCompanySchema>;

export default function ManagedCompanies() {
  const { getManagedCompaniesByCompanyId, deleteManagedCompany } = useManagedCompanies();

  const [managedCompaniesState, setManagedCompaniesState] =
    useState<ManagedCompaniesState>({
      companies: [],
      isFormOpen: false,
      isLoading: false,
    });
  const [dataGridPagination, setDataGridPagination] = useState<Pagination>({
    limit: MAX_API_PAGE_SIZE,
    page: 1,
  });

  const form = useForm<SelectedCompanyDTO>({
    resolver: yupResolver(selectedCompanySchema),
    mode: 'onSubmit',
    defaultValues: { company: null },
  });

  const companyId = form.watch('company')?.id ?? null;

  const fetchManagedCompanies = useCallback(async () => {
    if (!companyId) {
      return;
    }

    try {
      setManagedCompaniesState((state) => ({ ...state, isLoading: true }));

      const response = await getManagedCompaniesByCompanyId({
        companyId,
        pagination: dataGridPagination,
      });

      if (response) {
        setManagedCompaniesState({
          companies: response.data,
          isFormOpen: false,
          isLoading: false,
          pagination: response.pagination,
        });
      } else {
        throw new Error('Failed to fetch managed companies');
      }
    } catch {
      setManagedCompaniesState({
        companies: [],
        isFormOpen: false,
        isLoading: false,
      });
    }
  }, [companyId, dataGridPagination]);

  const handleManageAccountsClick = useCallback(() => {
    setManagedCompaniesState((state) => ({ ...state, isFormOpen: true }));
  }, []);

  const handleCloseManageAccountsForm = useCallback(
    (shouldRefetch?: true) => {
      setManagedCompaniesState((state) => ({ ...state, isFormOpen: false }));

      if (shouldRefetch) {
        fetchManagedCompanies();
      }
    },
    [fetchManagedCompanies],
  );

  const handleDeleteManagedCompanyClick = useCallback(
    (managedCompanyId: string) => {
      if (!companyId) {
        return;
      }

      deleteManagedCompany({ companyId, managedCompanyId }).then(() => {
        fetchManagedCompanies();
      });
    },
    [companyId, fetchManagedCompanies],
  );

  const columns = useMemo<Array<DataTableColumn<Company>>>(
    () => [
      {
        field: 'companyName',
        headerName: t('administration.managed_companies.grid_columns.company_name'),
        valueGetter: (company) => company.legalName,
      },
      actionsColumn((company) => (
        <Tooltip title={t('administration.managed_companies.grid_actions.delete')}>
          <IconButton
            onClick={() => handleDeleteManagedCompanyClick(company.id)}
            size="small"
          >
            <Close />
          </IconButton>
        </Tooltip>
      )),
    ],
    [handleDeleteManagedCompanyClick],
  );

  useEffect(() => {
    fetchManagedCompanies();
  }, [companyId, fetchManagedCompanies]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <CompanySelectorFormField
          clearable
          control={form.control}
          errors={form.formState.errors}
          hideLabel
          inputProps={{ placeholder: `${t('form_fields.company_selector_hint')}` }}
          name="company"
          sx={{ width: '540px' }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleManageAccountsClick} disabled={!companyId}>
            {t('administration.managed_companies.manage_accounts_form.open_form_cta')}
          </Button>
        </Box>

        <DataTable
          id="managed-companies-grid"
          columns={columns}
          headerTrailingComponent={
            <DataGridPager
              isFetching={managedCompaniesState.isLoading}
              onNavigate={(link) => {
                if (link === 'after') {
                  setDataGridPagination((state) => ({
                    page: state.page + 1,
                    limit: MAX_API_PAGE_SIZE,
                    after: managedCompaniesState.pagination?.after,
                  }));
                } else if (link === 'before') {
                  setDataGridPagination((state) => ({
                    page: state.page - 1,
                    limit: MAX_API_PAGE_SIZE,
                    before: managedCompaniesState.pagination?.before,
                  }));
                } else {
                  setDataGridPagination({ limit: MAX_API_PAGE_SIZE, page: 1 });
                }
              }}
              pagination={{
                ...dataGridPagination,
                before: managedCompaniesState.pagination?.before,
                after: managedCompaniesState.pagination?.after,
              }}
            />
          }
          loading={managedCompaniesState.isLoading}
          rows={managedCompaniesState.companies}
        />
      </Box>
      <ManageAccountsForm
        companyId={companyId ?? undefined}
        isOpen={managedCompaniesState.isFormOpen}
        managedCompanies={managedCompaniesState.companies}
        onClose={handleCloseManageAccountsForm}
      />
    </>
  );
}
